import CheckboxListInput from 'components/forms/inputs/rhf-inputs/CheckboxListInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { StarredFields } from 'components/forms/inputs/rhf-inputs/InputWrapper';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { Control, SetFieldValue, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useUpdateAssessmentMutation, useUpdateIdentityMutation } from 'store/api/client-identifications';
import { ClassifierData } from 'types/classifiers';
import { ClientIdentificationDto, ClientIdentificationPanelDto, PepAnswer } from 'types/clientIdentification';
import { t } from 'utils/getTranslations';
import { assessmentUpdate } from '../assessmentUpdate';
import { classifierOptions, identityFieldsToForm, identityFormToFields, IdentityFormValues } from '../identityForm';
import { isRequiredClientItem } from '../missingItems';
import { AnswerRadio } from './AnswerRadio';
import { Value } from './Block';
import { ClientParty } from './ClientAndTierBlock';
import { pepQuestionId } from './ClientDetailsBlock';
import { FieldRow, IdentityItemInputs } from './IdentityItemInputs';
import { successAlert } from 'utils/alert';

interface FormValues extends IdentityFormValues {
    sourceOfFundsCodes: string[];
    sourceOfFundsNote: string;
    relevantBanks: string;
    pepAnswer: PepAnswer | null;
    pepDetails: string;
}

interface Props {
    contractId: number;
    client: ClientParty;
    assessment: ClientIdentificationDto;
    panel: ClientIdentificationPanelDto;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
}

/** The values are the client identification's own and never touch the contact; a blank is recorded as a blank. */
export function ClientDetailsEditModal({ contractId, client, assessment, panel, classifiers, onClose }: Props) {
    const countries = classifierOptions(classifiers['COUNTRY']);
    const employmentStatuses = classifierOptions(classifiers['EMPLOYMENT_STATUS']);
    const sourceOfFundsOptions = classifierOptions(classifiers['SOURCE_OF_FUNDS']).options.filter(
        (option) =>
            !option.parameters?.partyFilterTypes || option.parameters.partyFilterTypes.includes(panel.clientPartyType)
    );
    const intl = useIntl();
    const [updateIdentity, { isLoading: isSavingIdentity }] = useUpdateIdentityMutation();
    const [updateAssessment, { isLoading: isSavingAnswers }] = useUpdateAssessmentMutation();

    const { control, setValue, handleSubmit } = useForm<FormValues>({
        defaultValues: {
            ...identityFieldsToForm(panel.clientFields, countries.optionOf, employmentStatuses.optionOf),
            sourceOfFundsCodes: panel.sourceOfFunds.codes,
            sourceOfFundsNote: panel.sourceOfFunds.note ?? '',
            relevantBanks: panel.sourceOfFunds.relevantBanks ?? '',
            pepAnswer: panel.pep.answer,
            pepDetails: panel.pep.details ?? '',
        },
    });
    const pepAnswer = useWatch({ control, name: 'pepAnswer' });
    const sourceOfFundsCodes = useWatch({ control, name: 'sourceOfFundsCodes' });
    const asksSourceOfFundsNote = sourceOfFundsOptions.some(
        (option) => option.parameters?.requiresNote && sourceOfFundsCodes.includes(option.value)
    );
    const asksOccupation = panel.clientItems.some((item) => item.key === 'OCCUPATION');

    const submit = handleSubmit(async (values) => {
        try {
            await updateIdentity({
                id: contractId,
                partyId: panel.clientPartyId,
                fields: identityFormToFields(values, panel.clientFields),
            }).unwrap();
            if (panel.asksSourceOfFunds || panel.asksPep) {
                await updateAssessment({
                    id: contractId,
                    ...assessmentUpdate(assessment, {
                        ...(panel.asksSourceOfFunds
                            ? {
                                  sourceOfFundsCodes: values.sourceOfFundsCodes,
                                  sourceOfFundsNote: values.sourceOfFundsNote.trim() || null,
                                  relevantBanks: values.relevantBanks.trim() || null,
                              }
                            : {}),
                        ...(panel.asksPep
                            ? {
                                  pepAnswer: values.pepAnswer,
                                  pepDetails: values.pepAnswer === 'YES' ? values.pepDetails.trim() || null : null,
                              }
                            : {}),
                    }),
                }).unwrap();
            }
        } catch {
            // The API layer has shown the server's reason; the popup stays open with what was typed.
            return;
        }
        successAlert(t('contracts.contract-updated'));
        onClose();
    });

    const identityContext = {
        // The form holds the identity values and the answers; the item inputs read the identity part.
        control: control as unknown as Control<IdentityFormValues>,
        setValue: setValue as unknown as SetFieldValue<IdentityFormValues>,
        partyType: panel.clientPartyType,
        countries: countries.options,
        employmentStatuses: employmentStatuses.options,
        isRequired: (itemKey: string) => isRequiredClientItem(itemKey, panel.tierCode),
    };

    return (
        <>
            <ModalHeader
                title={t('client-identification.edit-name', { name: client.name })}
                subtitle={t('client-identification.client')}
                onCancel={onClose}
            />
            <form
                noValidate
                className="flex min-h-0 flex-col gap-4 overflow-y-auto p-6"
                onSubmit={(event) => void submit(event)}
            >
                {panel.clientItems.map((item) => (
                    <IdentityItemInputs key={item.key} itemKey={item.key} context={identityContext} />
                ))}
                {asksOccupation && panel.employers.length ? (
                    <Value label={t('client-identification.employers')}>
                        {intl.formatList(panel.employers, { type: 'unit' })}
                    </Value>
                ) : null}
                {panel.asksSourceOfFunds ? (
                    <div className="flex flex-col gap-3">
                        <StarredFields value>
                            <CheckboxListInput
                                control={control}
                                name="sourceOfFundsCodes"
                                label="client-identification.item.SOURCE_OF_FUNDS"
                                options={sourceOfFundsOptions.map(({ value, label }) => ({ value, label }))}
                            />
                        </StarredFields>
                        <FieldRow>
                            <StarredFields value={asksSourceOfFundsNote}>
                                <TextInput
                                    control={control}
                                    name="sourceOfFundsNote"
                                    label="client-identification.note"
                                />
                            </StarredFields>
                            <TextInput
                                control={control}
                                name="relevantBanks"
                                label="client-identification.relevant-banks"
                            />
                        </FieldRow>
                        <p className="text-caption text-foreground-secondary m-0">
                            {t('client-identification.source-of-funds-hint')}
                        </p>
                    </div>
                ) : null}
                {panel.asksPep ? (
                    <>
                        <AnswerRadio<PepAnswer>
                            label={t('client-identification.item.PEP')}
                            description={t(pepQuestionId(panel.clientPartyType))}
                            required
                            inline
                            value={pepAnswer}
                            onChange={(answer) => setValue('pepAnswer', answer, { shouldDirty: true })}
                            options={[
                                { value: 'NO', label: t('no') },
                                { value: 'YES', label: t('yes') },
                            ]}
                        />
                        {pepAnswer === 'YES' ? (
                            <StarredFields value>
                                <TextAreaInput
                                    control={control}
                                    name="pepDetails"
                                    label="client-identification.pep-details"
                                    minRows={2}
                                />
                            </StarredFields>
                        ) : null}
                    </>
                ) : null}
            </form>
            <ModalFooter
                onCancel={onClose}
                onSave={() => void submit()}
                disableSave={isSavingIdentity || isSavingAnswers}
            />
        </>
    );
}
